import { useMemo, useState, memo, useCallback } from 'react';
import { Badge, Card, Nav } from 'react-bootstrap';
import { FaChevronDown, FaChevronRight, FaMapMarkerAlt, FaWalking } from 'react-icons/fa';
import './DetailNearbyPlaces.css';
import { NEARBY_CATEGORY_OPTIONS } from '../../components/sellProperty.constants';

const ALL_CATEGORY = 'Semua';
const DEFAULT_VISIBLE_COUNT = 5;

const normalizeText = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return String(value).trim();
};

const normalizeCategory = (value) => {
    const normalized = normalizeText(value);
    return normalized || 'Lainnya';
};

const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return Number.POSITIVE_INFINITY;

    const parsed = Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const sortNearbyPlaces = (items) => {
    return [...items].sort((a, b) => {
        const sortOrderA = Number.isFinite(Number(a?.sort_order)) ? Number(a.sort_order) : 0;
        const sortOrderB = Number.isFinite(Number(b?.sort_order)) ? Number(b.sort_order) : 0;

        if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;

        const distanceA = toNumber(a?.distance_km);
        const distanceB = toNumber(b?.distance_km);

        if (distanceA !== distanceB) return distanceA - distanceB;

        const nameA = normalizeText(a?.name).toLowerCase();
        const nameB = normalizeText(b?.name).toLowerCase();

        return nameA.localeCompare(nameB, 'id', { sensitivity: 'base' });
    });
};

const groupByCategory = (items) => {
    const groups = items.reduce((acc, place) => {
        const category = normalizeCategory(place?.category);

        if (!acc[category]) acc[category] = [];
        acc[category].push(place);

        return acc;
    }, {});

    Object.keys(groups).forEach((category) => {
        groups[category] = sortNearbyPlaces(groups[category]);
    });

    return groups;
};

const NearbyPlaceItem = memo(function NearbyPlaceItem({ place }) {
    return (
        <article className="detail-nearby-item">
            <div className="detail-nearby-icon" aria-hidden="true">
                <FaWalking />
            </div>

            <div className="detail-nearby-content">
                <div className="detail-nearby-top">
                    <span className="detail-nearby-category">{place.category || 'Lainnya'}</span>

                    {place.distance_km ? <span className="detail-nearby-distance">{place.distance_km} km</span> : null}
                </div>

                <h4 className="detail-nearby-name">{place.name || '-'}</h4>

                <div className="detail-nearby-address">
                    <FaMapMarkerAlt aria-hidden="true" />
                    <span>{place.address || '-'}</span>
                </div>
            </div>
        </article>
    );
});

const DetailNearbyPlaces = ({ nearbyPlaces = [] }) => {
    const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);
    const [expandedByCategory, setExpandedByCategory] = useState({});

    const groupedData = useMemo(() => groupByCategory(nearbyPlaces), [nearbyPlaces]);

    const categoryTabs = useMemo(() => {
        const optionOrder = (NEARBY_CATEGORY_OPTIONS || []).map((item) => item?.value).filter(Boolean);
        const presentCategories = Object.keys(groupedData);

        const orderedCategories = [
            ...optionOrder.filter((category) => presentCategories.includes(category)),
            ...presentCategories.filter((category) => !optionOrder.includes(category)),
        ];

        return [ALL_CATEGORY, ...orderedCategories];
    }, [groupedData]);

    const activePlaces = useMemo(() => {
        if (activeCategory === ALL_CATEGORY) {
            return sortNearbyPlaces(nearbyPlaces);
        }

        return groupedData[activeCategory] || [];
    }, [activeCategory, nearbyPlaces, groupedData]);

    const isExpanded = Boolean(expandedByCategory[activeCategory]);
    const hasMoreThanFive = activePlaces.length > DEFAULT_VISIBLE_COUNT;

    const visiblePlaces = useMemo(() => {
        if (!hasMoreThanFive) return activePlaces;
        if (isExpanded) return activePlaces;
        return activePlaces.slice(0, DEFAULT_VISIBLE_COUNT);
    }, [activePlaces, hasMoreThanFive, isExpanded]);

    const activeCount = activePlaces.length;

    const handleCategoryChange = useCallback((selectedKey) => {
        setActiveCategory(selectedKey || ALL_CATEGORY);
    }, []);

    const handleToggleExpanded = useCallback(() => {
        setExpandedByCategory((prev) => ({
            ...prev,
            [activeCategory]: !Boolean(prev[activeCategory]),
        }));
    }, [activeCategory]);

    if (!nearbyPlaces.length) return null;

    return (
        <Card className="detail-nearby-card">
            <Card.Body className="detail-nearby-card__body">
                <div className="detail-nearby-header">
                    <div className="detail-nearby-header__content">
                        <div className="detail-nearby-kicker">Fasilitas Sekitar</div>
                        <h3 className="section-title detail-nearby-title">Nearby Places</h3>
                        <p className="detail-nearby-subtitle">
                            Pilih kategori untuk melihat tempat terdekat di sekitar properti.
                        </p>
                    </div>

                    <div className="detail-nearby-summary">
                        <Badge bg="light" text="dark" className="detail-nearby-summary__badge">
                            {activeCount} data
                        </Badge>
                    </div>
                </div>

                <div className="detail-nearby-tabs-wrap" aria-label="Filter nearby places berdasarkan kategori">
                    <Nav
                        variant="pills"
                        activeKey={activeCategory}
                        onSelect={handleCategoryChange}
                        className="detail-nearby-tabs"
                    >
                        {categoryTabs.map((category) => {
                            const count =
                                category === ALL_CATEGORY ? nearbyPlaces.length : (groupedData[category] || []).length;

                            return (
                                <Nav.Item key={category} className="detail-nearby-tabs__item">
                                    <Nav.Link eventKey={category} className="detail-nearby-tab">
                                        <span className="detail-nearby-tab__label">{category}</span>
                                        <Badge bg="light" text="dark" className="detail-nearby-tab__badge">
                                            {count}
                                        </Badge>
                                    </Nav.Link>
                                </Nav.Item>
                            );
                        })}
                    </Nav>
                </div>

                {visiblePlaces.length ? (
                    <>
                        <div className="detail-nearby-list">
                            {visiblePlaces.map((place, index) => (
                                <NearbyPlaceItem
                                    key={place.id || `${place.category || 'Lainnya'}-${place.name || 'place'}-${index}`}
                                    place={place}
                                />
                            ))}
                        </div>

                        {hasMoreThanFive ? (
                            <div className="detail-nearby-more">
                                <button
                                    type="button"
                                    className="detail-nearby-more__button"
                                    onClick={handleToggleExpanded}
                                    aria-expanded={isExpanded}
                                >
                                    <span>{isExpanded ? 'Tampilkan Lebih Sedikit' : 'Lihat Lebih Banyak'}</span>
                                    {isExpanded ? (
                                        <FaChevronDown aria-hidden="true" />
                                    ) : (
                                        <FaChevronRight aria-hidden="true" />
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className="detail-nearby-empty">
                        <div className="detail-nearby-empty__icon" aria-hidden="true">
                            <FaWalking />
                        </div>
                        <div className="detail-nearby-empty__title">Data belum tersedia</div>
                        <div className="detail-nearby-empty__text">
                            Tidak ada nearby place pada kategori yang dipilih.
                        </div>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default DetailNearbyPlaces;
